import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import styles from "../../_css/Navbar.module.css";
import {
  LucideLogOut,
  MenuIcon,
  XIcon,
  ListIcon,
  UsersIcon,
  LayoutDashboard,
  BuildingIcon,
  Tag,
  FileIcon,
  DollarSign,
  Coins,
  UserCircle,
  Search,
  MessageSquareReply,
  Trophy,
  Layers,
  Play,
  Download,
} from "lucide-react";
import { deleteAuthToken, notifySuccess } from "../../utils/helper";
import { useDispatch, useSelector } from "react-redux";
import { removeUserInformation } from "../../_store/reducers/userReducer";
import { clearContext } from "../../_store/reducers/contextReducer";
import getRole from "../../hooks/getRole";

const LOGO_SRC = `${process.env.PUBLIC_URL || ""}/logo.png`;
const ICON_LOGO_SRC = `${process.env.PUBLIC_URL || ""}/ICON_1_LOGO_RGB.svg`;

const Navbar = () => {
  const role = getRole();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const userInfo = useSelector((state) => state.user);
  console.log("userInfo", userInfo);
  const context = useSelector((state) => state.context);

  const projectsNavEnabled = Boolean(context?.brand_id);
  const simulationsNavEnabled = Boolean(context?.project_id);

  const isSuperAdmin = String(role || "").toLowerCase() === "super_admin";

  const costNavPath = isSuperAdmin
    ? `/cost?agency_id=${userInfo?.agencyId}`
    : "/cost";

  // Footer entry: super admins only. Agency admins find "Credits" in the main menu (INV-22).
  const showCostNav = isSuperAdmin;
  const costNavLabel = isSuperAdmin ? "Cost" : "Credits";
  const CostNavIcon = isSuperAdmin ? DollarSign : Coins;

  const navBasePath = (path) =>
    typeof path === "string" ? path.split("?")[0] : "";

  const isNavDisabled = (item) => {
    const base = navBasePath(item.path);
    if (base === "/projects") return !projectsNavEnabled;
    if (base === "/simulations") return !simulationsNavEnabled;
    return false;
  };

  const disabledNavHintText = (path) => {
    const base = navBasePath(path);
    if (base === "/projects")
      return "Please select a brand to access Projects.";
    if (base === "/simulations")
      return "Please select a project to access Simulations.";
    return "";
  };

  const handleLogout = () => {
    setMobileOpen(false);
    deleteAuthToken();
    dispatch(removeUserInformation());
    dispatch(clearContext());
    navigate("/login");
    notifySuccess("Logged out successfully");
  };

  const userMenu = [
    { label: "Start", icon: <LayoutDashboard size={16} />, path: "/" },
    { label: "Brands", icon: <Tag size={16} />, path: "/brands" },
    {
      label: "Projects",
      icon: <FileIcon size={16} />,
      path: `/projects?brand_id=${context?.brand_id}`,
    },
    {
      label: "Simulations",
      icon: <LayoutDashboard size={16} />,
      path: `/simulations?project_id=${context?.project_id}&brand_id=${context?.brand_id}`,
    },
  ];

  const adminMenu = [
    { label: "Start", icon: <LayoutDashboard size={16} />, path: "/" },
    { label: "Users", icon: <UsersIcon size={16} />, path: "/invite" },
    // INV-22: in the main menu - in the footer it sat below the visible
    // sidebar once the simulation sub-menu was open.
    { label: "Credits", icon: <Coins size={16} />, path: "/cost" },
    { label: "Brands", icon: <Tag size={16} />, path: "/brands" },
    {
      label: "Projects",
      icon: <FileIcon size={16} />,
      path: `/projects?brand_id=${context?.brand_id}`,
    },
    {
      label: "Simulations",
      icon: "◈",
      path: `/simulations?project_id=${context?.project_id}&brand_id=${context?.brand_id}`,
    },
  ];

  const superAdminMenu = [
    {
      label: "Start",
      icon: <LayoutDashboard size={16} />,
      path: `/?agency_id=${userInfo?.agencyId}`,
    },
    {
      label: "Users",
      icon: <UsersIcon size={16} />,
      path: `/users?agency_id=${userInfo?.agencyId}`,
    },
    { label: "Agencies", icon: <BuildingIcon size={16} />, path: `/agencies` },
    {
      label: "Brands",
      icon: <Tag size={16} />,
      path: `/brands?agency_id=${userInfo?.agencyId}`,
    },
    {
      label: "Projects",
      icon: <FileIcon size={16} />,
      path: `/projects?brand_id=${context?.brand_id}&agency_id=${userInfo?.agencyId}`,
    },
    {
      label: "Simulations",
      icon: <LayoutDashboard size={16} />,
      path: `/simulations?project_id=${context?.project_id}&brand_id=${context?.brand_id}&agency_id=${userInfo?.agencyId}`,
    },
  ];

  const menu =
    role == "user" ? userMenu : role == "admin" ? adminMenu : superAdminMenu;

  // Project detail sub-nav for the currently active simulation
  const projectDetailMatch = location.pathname.match(
    /^\/simulations\/details\/([^/]+)(?:\/([^/]+))?(?:\/.*)?$/,
  );
  const urlSimulationId = projectDetailMatch?.[1] ?? null;
  const activeSimulationId =
    urlSimulationId ??
    context?.simulation_id ??
    userInfo?.activeSimulation?.id ??
    null;
  const activeSimulationDisplayName =
    (context?.simulation_name && String(context.simulation_name).trim()) ||
    (userInfo?.activeSimulation?.name &&
      String(userInfo.activeSimulation.name).trim()) ||
    "";
  const currentProjectTab = projectDetailMatch?.[2] ?? null;
  const projectSubTabsforSuperAdmin = [
    {
      id: "persona",
      path: `/simulations/details/${activeSimulationId}/persona?simulation_id=${activeSimulationId}&project_id=${context?.project_id}&brand_id=${context?.brand_id}&agency_id=${userInfo?.agencyId}`,
      label: "Audience Profile",
      icon: <UserCircle size={16} />,
    },
    {
      id: "queries",
      path: `/simulations/details/${activeSimulationId}/queries?simulation_id=${activeSimulationId}&project_id=${context?.project_id}&brand_id=${context?.brand_id}&agency_id=${userInfo?.agencyId}`,
      label: "Queries Workshop",
      icon: <Search size={16} />,
    },
    {
      id: "response",
      path: `/simulations/details/${activeSimulationId}/response?simulation_id=${activeSimulationId}&project_id=${context?.project_id}&brand_id=${context?.brand_id}&agency_id=${userInfo?.agencyId}`,
      label: "Response Inspector",
      icon: <MessageSquareReply size={16} />,
    },
    {
      id: "competitive",
      path: `/simulations/details/${activeSimulationId}/competitive?simulation_id=${activeSimulationId}&project_id=${context?.project_id}&brand_id=${context?.brand_id}&agency_id=${userInfo?.agencyId}`,
      label: "Competitive Insights",
      icon: <Trophy size={16} />,
    },
    {
      id: "crossplatform",
      path: `/simulations/details/${activeSimulationId}/crossplatform?simulation_id=${activeSimulationId}&project_id=${context?.project_id}&brand_id=${context?.brand_id}&agency_id=${userInfo?.agencyId}`,
      label: "Cross-Platform Analysis",
      icon: <Layers size={16} />,
    },
    {
      id: "run",
      path: `/simulations/details/${activeSimulationId}/run?simulation_id=${activeSimulationId}&project_id=${context?.project_id}&brand_id=${context?.brand_id}&agency_id=${userInfo?.agencyId}`,
      label: "Runs",
      icon: <Play size={16} />,
    },
    {
      id: "download",
      path: `/simulations/details/${activeSimulationId}/download?simulation_id=${activeSimulationId}&project_id=${context?.project_id}&brand_id=${context?.brand_id}&agency_id=${userInfo?.agencyId}`,
      label: "Download Results",
      icon: <Download size={16} />,
    },
  ];
  const projectSubTabsforAdmin = [
    {
      id: "persona",
      path: `/simulations/details/${activeSimulationId}/persona?simulation_id=${activeSimulationId}&project_id=${context?.project_id}&brand_id=${context?.brand_id}`,
      label: "Audience Profile",
      icon: <UserCircle size={16} />,
    },
    {
      id: "queries",
      path: `/simulations/details/${activeSimulationId}/queries?simulation_id=${activeSimulationId}&project_id=${context?.project_id}&brand_id=${context?.brand_id}`,
      label: "Queries Workshop",
      icon: <Search size={16} />,
    },
    {
      id: "response",
      path: `/simulations/details/${activeSimulationId}/response?simulation_id=${activeSimulationId}&project_id=${context?.project_id}&brand_id=${context?.brand_id}`,
      label: "Response Inspector",
      icon: <MessageSquareReply size={16} />,
    },
    {
      id: "competitive",
      path: `/simulations/details/${activeSimulationId}/competitive?simulation_id=${activeSimulationId}&project_id=${context?.project_id}&brand_id=${context?.brand_id}`,
      label: "Competitive Insights",
      icon: <Trophy size={16} />,
    },
    {
      id: "crossplatform",
      path: `/simulations/details/${activeSimulationId}/crossplatform?simulation_id=${activeSimulationId}&project_id=${context?.project_id}&brand_id=${context?.brand_id}`,
      label: "Cross-Platform Analysis",
      icon: <Layers size={16} />,
    },
    {
      id: "run",
      path: `/simulations/details/${activeSimulationId}/run?simulation_id=${activeSimulationId}&project_id=${context?.project_id}&brand_id=${context?.brand_id}`,
      label: "Runs",
      icon: <Play size={16} />,
    },
    {
      id: "download",
      path: `/simulations/details/${activeSimulationId}/download?simulation_id=${activeSimulationId}&project_id=${context?.project_id}&brand_id=${context?.brand_id}`,
      label: "Download Results",
      icon: <Download size={16} />,
    },
  ];

  const projectSubTabs =
    role == "super_admin"
      ? projectSubTabsforSuperAdmin
      : projectSubTabsforAdmin;

  const navItem = (item, isMobile = false) => {
    if (item.label === "Logout") return null;
    const disabled = isNavDisabled(item);
    const baseClass = isMobile ? styles.mobileLink : styles.navLink;
    const itemBasePath = item.path.split("?")[0];
    if (disabled) {
      return (
        <li key={item.label} className={styles.navItem}>
          <span className={styles.disabledNavHoverWrap}>
            <span
              className={`${baseClass} ${styles.navItemDisabled}`}
              aria-disabled="true"
            >
              <span className={styles.navIconWrap}>{item.icon}</span>
              {item.label}
            </span>
            <span className={styles.disabledNavHint} role="tooltip">
              {disabledNavHintText(item.path)}
            </span>
          </span>
        </li>
      );
    }
    return (
      <li key={item.label} className={styles.navItem}>
        <NavLink
          to={item.path}
          className={({ isActive }) => {
            return item.primary
              ? isActive
                ? `${styles.primaryButton} ${styles.primaryActive}`
                : styles.primaryButton
              : isMobile
                ? isActive
                  ? `${styles.mobileLink} ${styles.mobileLinkActive}`
                  : styles.mobileLink
                : isActive
                  ? `${styles.navLink} ${styles.active}`
                  : styles.navLink;
          }}
          end={itemBasePath === "/" || itemBasePath === "/simulations"}
          onClick={() => setMobileOpen(false)}
        >
          <span className={styles.navIconWrap}>{item.icon}</span>
          {item.label}
        </NavLink>
      </li>
    );
  };

  return (
    <>
      {/* Mobile: dropdown trigger in top right */}
      <button
        type="button"
        className={styles.mobileTrigger}
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label="Toggle menu"
      >
        {mobileOpen ? <XIcon /> : <MenuIcon />}
      </button>

      {/* Mobile dropdown overlay */}
      {mobileOpen && (
        <div
          className={styles.mobileOverlay}
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile dropdown panel */}
      <div
        className={`${styles.mobileDropdown} dm-sans-100 ${mobileOpen ? styles.mobileDropdownOpen : ""}`}
      >
        <div className={styles.mobileLogo}>
          <div className={styles.logoRow}>
            <img
              src={ICON_LOGO_SRC}
              alt=""
              className={styles.iconLogo}
              aria-hidden="true"
            />
            <img src={LOGO_SRC} alt="Search AI" className={styles.logoImage} />
          </div>
        </div>
        <ul className={styles.mobileNavList}>
          {menu.map((item) => navItem(item, true))}
          {activeSimulationId && (
            <>
              <li
                className={styles.navItem}
                style={{
                  marginTop: 8,
                  paddingTop: 8,
                  borderTop: "1px solid rgba(0,0,0,0.08)",
                }}
              >
                <span
                  className={styles.mobileLink}
                  style={{
                    color: "#363636",
                    fontSize: 12,
                    fontWeight: 600,
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={activeSimulationDisplayName || undefined}
                >
                  {activeSimulationDisplayName || "Simulation"}
                </span>
              </li>
              {projectSubTabs.map((t) => (
                <li key={t.id} className={styles.navItem}>
                  <NavLink
                    to={t.path}
                    className={({ isActive }) =>
                      isActive ||
                      (currentProjectTab && currentProjectTab === t.id)
                        ? `${styles.mobileLink} ${styles.mobileLinkActive}`
                        : styles.mobileLink
                    }
                    onClick={() => setMobileOpen(false)}
                  >
                    <span className={styles.navIconWrap}>{t.icon}</span>
                    {t.label}
                  </NavLink>
                </li>
              ))}
            </>
          )}
        </ul>
        <div className={styles.mobileFooter}>
          {showCostNav && (
            <button
              type="button"
              className={
                location.pathname === "/cost" ||
                location.pathname.startsWith("/cost/")
                  ? `${styles.mobileLinkButton} ${styles.mobileLinkActive}`
                  : styles.mobileLinkButton
              }
              onClick={() => {
                setMobileOpen(false);
                navigate(costNavPath);
              }}
            >
              <span className={styles.navIconWrap}>
                <CostNavIcon />
              </span>
              {costNavLabel}
            </button>
          )}
          <button
            type="button"
            className={styles.mobileLinkButton}
            onClick={handleLogout}
          >
            <span className={styles.navIconWrap}>
              <LucideLogOut />
            </span>
            Logout
          </button>
        </div>
      </div>

      {/* Desktop: vertical sidebar 20% width */}
      <nav className={`${styles.sidebar} dm-sans-100`}>
        <div className={styles.sidebarLogoBar}>
          <div className={styles.logoRow}>
            <img
              src={ICON_LOGO_SRC}
              alt=""
              className={styles.iconLogo}
              aria-hidden="true"
            />
            <img
              src={LOGO_SRC}
              alt="Search AI"
              className={styles.logoImage}
              style={{ height: 29, width: "auto" }}
            />
          </div>
        </div>
        <ul className={styles.navList}>
          {menu.map((item) => navItem(item, false))}
          {activeSimulationId && (
            <>
              <li
                className={styles.navItem}
                style={{
                  marginTop: 8,
                  paddingTop: 12,
                  borderTop: "1px solid rgba(0,0,0,0.08)",
                }}
              >
                <span
                  className={styles.navLink}
                  style={{
                    color: "#363636",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "default",
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={activeSimulationDisplayName || undefined}
                >
                  {activeSimulationDisplayName || "Simulation"}
                </span>
              </li>
              {projectSubTabs.map((t) => (
                <li key={t.id} className={styles.navItem}>
                  <NavLink
                    to={t.path}
                    className={({ isActive }) =>
                      isActive ||
                      (currentProjectTab && currentProjectTab === t.id)
                        ? `${styles.navLink} ${styles.active}`
                        : styles.navLink
                    }
                  >
                    <span className={styles.navIconWrap}>{t.icon}</span>
                    {t.label}
                  </NavLink>
                </li>
              ))}
            </>
          )}
        </ul>
        <div className={styles.sidebarFooter}>
          {showCostNav && (
            <button
              type="button"
              className={
                location.pathname === "/cost" ||
                location.pathname.startsWith("/cost/")
                  ? `${styles.buttonLink} ${styles.active}`
                  : styles.buttonLink
              }
              onClick={() => navigate(costNavPath)}
            >
              <span className={styles.navIconWrap}>
                <CostNavIcon />
              </span>
              {costNavLabel}
            </button>
          )}
          <button
            type="button"
            className={styles.buttonLink}
            onClick={handleLogout}
          >
            <span className={styles.navIconWrap}>
              <LucideLogOut />
            </span>
            Logout
          </button>
        </div>
      </nav>
      {/* <footer className={styles.footer}>

      </footer> */}
    </>
  );
};

export default Navbar;
